// Shared shell for the advertiser Settings surface. Renders PublicHeader,
// the page heading, and a three-tab strip (Business / Team / Security) with
// an <Outlet /> for the active tab's sections.
//
// Consolidates what used to be two separate surfaces — `/:slug/my-team` and
// `/:slug/settings/security` — into `/:slug/settings/*`. The layout owns
// station branding + auth gating so each tab component is a plain
// sections-only page.

import React from 'react';
import { Navigate, NavLink, Outlet, useParams } from 'react-router-dom';
import { useAuth } from '../../components/self-service/auth/useAuth';
import { usePublisherConfig } from '../../data/publisherConfigs';
import PublicHeader from '../../components/self-service/PublicHeader';

interface Tab {
  to: string;
  label: string;
}

const SettingsLayout: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const { config } = usePublisherConfig(slug);
  const { isAuthenticated, selectedAdvertiser } = useAuth();
  const accent = config?.accentColor || config?.primaryColor;

  if (!isAuthenticated) {
    // Anon users hit the station's public landing rather than a bare
    // sign-in modal — this URL is reached from the signed-in dropdown and
    // callers should already have a session.
    return <Navigate to={slug ? `/${slug}` : '/'} replace />;
  }

  const tabs: Tab[] = [
    { to: 'business', label: 'Business' },
    { to: 'team', label: 'Team' },
    { to: 'security', label: 'Security' },
  ];

  const businessName = selectedAdvertiser?.advertiserName;

  return (
    <div className="min-h-screen bg-slate-50">
      <PublicHeader publisher={config ?? undefined} title="Settings" />
      <div className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-2xl font-bold text-slate-900 mb-1">Settings</h1>
        <p className="text-sm text-slate-600 mb-6">
          {businessName ? (
            <>
              Manage{' '}
              <span className="font-medium text-slate-900">{businessName}</span>
              's business details, team, and your sign-in.
            </>
          ) : (
            'Manage your business, team, and sign-in.'
          )}
        </p>

        <div className="border-b border-slate-200 mb-6">
          <nav className="-mb-px flex gap-6" aria-label="Settings sections">
            {tabs.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                className={({ isActive }) =>
                  [
                    'inline-flex items-center px-1 py-3 text-sm font-medium border-b-2 transition-colors',
                    isActive
                      ? 'text-slate-900'
                      : 'text-slate-500 hover:text-slate-900 border-transparent',
                  ].join(' ')
                }
                style={({ isActive }) =>
                  isActive
                    ? { borderColor: accent ?? '#0f172a' }
                    : undefined
                }
              >
                {tab.label}
              </NavLink>
            ))}
          </nav>
        </div>

        <Outlet />
      </div>
    </div>
  );
};

export default SettingsLayout;
